import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import { MEMBER_EVENT, memberLabel, memberPoints } from '../utils/memberScan';

/**
 * Checkout: a scanned loyalty membership card (the 'scan:member' event, see utils/scanActions.js) is mapped to this order as its customer
 * (POST update-order-customer, the call customer selection makes). It never applies or redeems anything: afterwards the checkout shows the
 * customer's points and the cashier ticks "Redeem loyalty points" as before.
 *   orderNumbers: the order(s) being paid, comma separated (from the checkout summary); without them the scan is left unhandled
 *   busy:         a payment is in progress: the scan is refused with a message
 *   onAttached(member): called once the order has its new customer
 */
export default function useScannedMember({ orderNumbers, busy, onAttached }) {
  const live = useRef({});
  live.current = { orderNumbers, busy, onAttached };

  useEffect(() => {
    const onScan = async (e) => {
      const detail = e.detail || {};
      if (!detail.member || detail.handled || !live.current.orderNumbers) return;
      detail.handled = true;
      const { member } = detail;
      if (live.current.busy) {
        toast.info('Wait for the payment to finish before changing the customer');
        return;
      }
      try {
        const res = await callAPI(apiUrls.UPDATE_ORDER_CUSTOMER, {}, 'POST', { order_numbers: live.current.orderNumbers, customer: member.customer_id }, {});
        if (res?.data?.status) {
          const pts = memberPoints(member);
          toast.success(pts > 0 ? `Member: ${memberLabel(member)}. Tick Redeem loyalty points to use them.` : `Member: ${memberLabel(member)}`);
          live.current.onAttached(member);
        } else {
          toast.error('Could not add the member to this order');
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Could not add the member to this order');
      }
    };
    window.addEventListener(MEMBER_EVENT, onScan);
    return () => window.removeEventListener(MEMBER_EVENT, onScan);
  }, []);
}
